"use client"

import { Receipt, Loader2, Download } from "lucide-react"
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import { Paper } from '@mui/material';
import { useGetTransactionsQuery, useLazyGetInvoiceQuery } from "@/app/store/api/authApi"
import { useState } from "react"
import { cn } from "@/lib/utils"
import { toast } from "sonner"

// Helper to format date
const formatDate = (dateString: string): string => {
    const date = new Date(dateString)
    return date.toLocaleDateString('en-US', {
        month: 'short',
        day: '2-digit',
        year: 'numeric'
    })
}

// Helper to format currency (showing as it is from backend)
const formatCurrency = (amount: number, currency: string): string => {
    return `${currency.toUpperCase()} ${amount}`
}

const formatPurchaseType = (type: string): string => {
    const typeLabels: Record<string, string> = {
        DECK_UNLOCK_PRO: "Pro Deck Unlock",
        DECK_UNLOCK_SHARE: "Share Access Unlock",
        CREDITS_REFILL: "Deck Refill"
    }

    if (typeLabels[type]) return typeLabels[type]

    return type
        .split('_')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
        .join(' ')
}

const ITEMS_PER_PAGE = 10

export function TransactionsTab() {
    const [page, setPage] = useState(1)
    const { data: transactionsData, isLoading, isFetching } = useGetTransactionsQuery({ 
        page, 
        limit: ITEMS_PER_PAGE 
    })

    const transactions = transactionsData?.transactions || []
    
    // Simple pagination handling for now as we don't have total count in initial response but added it to type
    const hasMore = transactions.length === ITEMS_PER_PAGE

    if (isLoading) {
        return (
            <div className="flex items-center justify-center h-64">
                <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
            </div>
        )
    }

    return (
        <div className="space-y-6 max-w-4xl mx-auto">
            <div className="flex items-center justify-between mb-4 px-1">
                <h3 className="font-bold text-sm text-slate-900">Payment History</h3>
            </div>

            <TableContainer component={Paper} sx={{ boxShadow: 'none', border: '1px solid #e2e8f0', borderRadius: '0.75rem' }}>
                <Table sx={{ fontFamily: '"Plus Jakarta Sans", sans-serif' }} aria-label="transactions table">
                    <TableHead>
                        <TableRow>
                            <TableCell sx={{ fontFamily: 'inherit', fontSize: '10px', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.05em', py: 1.5, pl: { xs: 2, md: 3 } }}>Date</TableCell>
                            <TableCell sx={{ fontFamily: 'inherit', fontSize: '10px', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.05em', py: 1.5 }}>Type</TableCell>
                            <TableCell sx={{ fontFamily: 'inherit', fontSize: '10px', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.05em', py: 1.5 }}>Status</TableCell>
                            <TableCell align="right" sx={{ fontFamily: 'inherit', fontSize: '10px', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.05em', py: 1.5 }}>Amount</TableCell>
                            <TableCell align="right" sx={{ fontFamily: 'inherit', fontSize: '10px', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.05em', py: 1.5, pr: { xs: 2, md: 3 } }}>Invoice</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {transactions.length > 0 ? (
                            transactions.map((tx) => (
                                <TransactionRow key={tx._id} transaction={tx} />
                            ))
                        ) : (
                            <TableRow>
                                <TableCell colSpan={6} sx={{ fontFamily: 'inherit', py: 4, textAlign: 'center', color: '#94a3b8' }}>
                                    No transactions found.
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            {/* Pagination Controls */}
            <div className="flex items-center justify-between mt-4 px-1">
                <button
                    disabled={page === 1 || isFetching}
                    onClick={() => setPage(p => Math.max(1, p - 1))}
                    className="px-3 py-1.5 text-xs font-semibold text-slate-600 bg-slate-100 hover:bg-slate-200 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                    Previous
                </button>
                <span className="text-[11px] font-bold text-slate-500 uppercase tracking-wider">Page {page}</span>
                <button
                    disabled={!hasMore || isFetching}
                    onClick={() => setPage(p => p + 1)}
                    className="px-3 py-1.5 text-xs font-semibold text-slate-600 bg-slate-100 hover:bg-slate-200 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                    Next
                </button>
            </div>
        </div>
    )
}

function TransactionRow({ transaction }: { transaction: any }) {
    const [getInvoice, { isFetching: isDownloading }] = useLazyGetInvoiceQuery()
    const statusColors: Record<string, string> = {
        captured: "bg-emerald-50 text-emerald-600 border-emerald-100",
        created: "bg-blue-50 text-blue-600 border-blue-100",
        failed: "bg-red-50 text-red-600 border-red-100",
        refunded: "bg-slate-50 text-slate-600 border-slate-100"
    }

    const status = (transaction.status || 'created').toLowerCase()

    const handleDownloadInvoice = async (e: React.MouseEvent) => {
        e.stopPropagation()
        try {
            const blob = await getInvoice({ transactionId: transaction._id }).unwrap()
            // Create a temporary link and trigger download
            const url = window.URL.createObjectURL(blob)
            const a = document.createElement('a')
            a.href = url
            a.download = `invoice-${transaction.invoiceNumber || transaction.razorPayOrderId || transaction._id}.pdf`
            document.body.appendChild(a)
            a.click()
            window.URL.revokeObjectURL(url)
            document.body.removeChild(a)
        } catch (error: any) {
            console.error("Failed to fetch invoice:", error)
            toast.error("Failed to download invoice. Please try again.")
        }
    }

    return (
        <TableRow
            sx={{
                '&:last-child td, &:last-child th': { border: 0 },
                cursor: 'pointer',
                transition: 'background-color 0.2s',
                '&:hover': { backgroundColor: '#f8fafc' }
            }}
        >
            <TableCell sx={{ fontFamily: 'inherit', fontSize: '11px', fontWeight: 600, color: '#64748b', py: 2, pl: { xs: 2, md: 3 }, borderColor: '#f1f5f9', whiteSpace: 'nowrap' }}>
                <div className="flex items-center gap-2 md:gap-3">
                    <div className="w-8 h-8 rounded-lg bg-slate-50 border border-slate-100 flex items-center justify-center text-slate-400 shrink-0">
                        <Receipt className="w-4 h-4" />
                    </div>
                    <span>{formatDate(transaction.paymentDate || transaction.createdAt)}</span>
                </div>
            </TableCell>
            <TableCell sx={{ fontFamily: 'inherit', fontSize: '11px', fontWeight: 600, color: '#64748b', py: 2, borderColor: '#f1f5f9' }}>
                {formatPurchaseType(transaction.purchaseType)}
            </TableCell>
            <TableCell sx={{ fontFamily: 'inherit', py: 2, borderColor: '#f1f5f9' }}>
                <span className={cn(
                    "inline-flex items-center px-2 py-0.5 rounded-full text-[9px] font-bold border",
                    statusColors[status] || statusColors.created
                )}>
                    {transaction.status?.toUpperCase() || 'CREATED'}
                </span>
            </TableCell>
            <TableCell align="right" sx={{ fontFamily: 'inherit', py: 2, borderColor: '#f1f5f9', whiteSpace: 'nowrap' }}>
                <span className="text-[13px] md:text-[14px] font-bold text-slate-900">
                    {formatCurrency(transaction.amount, transaction.currency)}
                </span>
            </TableCell>
            <TableCell align="right" sx={{ fontFamily: 'inherit', py: 2, pr: { xs: 2, md: 3 }, borderColor: '#f1f5f9' }}>
                {transaction.invoiceUrl ? (
                    <button 
                        disabled={isDownloading}
                        onClick={handleDownloadInvoice}
                        className="inline-flex items-center justify-center p-1.5 rounded-lg bg-blue-50 text-blue-600 border border-blue-100 hover:bg-blue-100 transition-colors disabled:opacity-50"
                        title="Download Invoice"
                    >
                        {isDownloading ? (
                            <Loader2 className="w-3.5 h-3.5 animate-spin" />
                        ) : (
                            <Download className="w-3.5 h-3.5" />
                        )}
                    </button>
                ) : (
                    <span className="text-[9px] font-bold text-slate-300">N/A</span>
                )}
            </TableCell>
        </TableRow>
    )
}
